import React from 'react';

interface BaseTextProps {
  children: React.ReactNode;
  className?: string;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
}

export function H1({ children, className = '', as = 'h1' }: BaseTextProps) {
  const Component = as;
  return (
    <Component className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight ${className}`}>
      {children}
    </Component>
  );
}

export function H2({ children, className = '', as = 'h2' }: BaseTextProps) {
  const Component = as;
  return (
    <Component className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight ${className}`}>
      {children}
    </Component>
  );
}

export function H3({ children, className = '', as = 'h3' }: BaseTextProps) {
  const Component = as;
  return (
    <Component className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-gray-900 tracking-tight ${className}`}>
      {children}
    </Component>
  );
}

export function H4({ children, className = '', as = 'h4' }: BaseTextProps) {
  const Component = as;
  return (
    <Component className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-gray-900 ${className}`}>
      {children}
    </Component>
  );
}

export function H5({ children, className = '', as = 'h5' }: BaseTextProps) {
  const Component = as;
  return (
    <Component className={`text-base sm:text-lg md:text-xl lg:text-2xl font-semibold text-gray-900 ${className}`}>
      {children}
    </Component>
  );
}

export function H6({ children, className = '', as = 'h6' }: BaseTextProps) {
  const Component = as;
  return (
    <Component className={`text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-900 ${className}`}>
      {children}
    </Component>
  );
}

interface TextProps extends BaseTextProps {
  variant?: 'body' | 'small' | 'caption' | 'overline' | 'code';
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black';
  color?: 'default' | 'muted' | 'subtle' | 'white';
}

export function Text({
  children,
  className = '',
  as = 'p',
  variant = 'body',
  weight = 'normal',
  color = 'default',
}: TextProps) {
  const Component = as;

  const variantClasses = {
    body: 'text-sm sm:text-base md:text-lg',
    small: 'text-xs sm:text-sm md:text-base',
    caption: 'text-xs sm:text-xs md:text-sm',
    overline: 'text-xs sm:text-xs md:text-sm uppercase tracking-wider',
    code: 'text-xs sm:text-sm md:text-base font-mono',
  };

  const weightClasses = {
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    extrabold: 'font-extrabold',
    black: 'font-black',
  };

  const colorClasses = {
    default: 'text-gray-700',
    muted: 'text-gray-600',
    subtle: 'text-gray-500',
    white: 'text-white',
  };

  return (
    <Component
      className={`${variantClasses[variant]} ${weightClasses[weight]} ${colorClasses[color]} ${className}`}
    >
      {children}
    </Component>
  );
}

export function Label({ children, className = '', htmlFor }: { children: React.ReactNode; className?: string; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className={`text-xs sm:text-sm md:text-base font-medium text-gray-700 ${className}`}>
      {children}
    </label>
  );
}

export function Code({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <code className={`px-1.5 py-0.5 rounded bg-gray-100 text-xs sm:text-sm md:text-base font-mono text-gray-800 ${className}`}>
      {children}
    </code>
  );
}

export function Subheading({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-base sm:text-lg md:text-xl font-medium text-gray-700 ${className}`}>
      {children}
    </p>
  );
}

export function Caption({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={`text-xs sm:text-sm md:text-base font-normal text-gray-500 ${className}`}>
      {children}
    </p>
  );
}

/**
 * Named text styles: the size + weight + colour combinations the app repeats.
 * Use these instead of re-typing the classes, so a role (a caption, a section
 * title) is defined once. Each is exactly the classes it replaced, so adopting
 * one changes nothing on screen. Form labels use FIELD_LABEL_CLASS
 * (shared/FormInput), not TEXT.label, because they also carry `block mb-1`.
 */
export const TEXT = {
  // headings
  titleLg: 'text-xl font-semibold text-gray-900',
  sectionTitle: 'text-lg font-semibold text-gray-900',
  cardTitle: 'text-base font-semibold text-gray-900',
  subheading: 'text-sm font-semibold text-gray-800',
  // body
  body: 'text-sm text-gray-700',
  bodyMuted: 'text-sm text-gray-600',
  bodyDark: 'text-sm text-gray-900',
  bodyLarge: 'text-base text-gray-900',
  bodyMedium: 'text-sm font-medium',
  bodyStrong: 'text-sm font-medium text-gray-900',
  bodyEmphasis: 'text-sm font-semibold text-gray-900',
  helper: 'text-sm text-gray-500',
  // labels
  label: 'text-sm font-medium text-gray-700',
  labelMuted: 'text-sm font-medium text-gray-600',
  labelStrong: 'text-sm font-semibold text-gray-700',
  labelInline: 'font-medium text-gray-700',
  /** The muted label above a read-only value on a detail panel ("Submitted", "Hours"). */
  detailLabel: 'text-sm font-medium text-gray-500',
  /** A small uppercase label for a panel or a group of fields. */
  overline: 'text-xs font-semibold uppercase tracking-wider text-gray-500',
  // inline emphasis (inherits size)
  strong: 'font-medium text-gray-900',
  emphasis: 'font-semibold text-gray-900',
  emphasisMuted: 'font-semibold text-gray-800',
  // small print
  caption: 'text-xs text-gray-500',
  captionMuted: 'text-xs text-gray-600',
  captionMedium: 'text-xs font-medium text-gray-600',
  captionStrong: 'text-xs font-semibold text-gray-500',
  eyebrow: 'text-xs font-medium text-gray-500',
  micro: 'text-2xs text-gray-500',
  // status
  warningText: 'text-sm text-amber-700',
  errorCaption: 'text-xs text-red-600',
  linkCaption: 'text-xs text-blue-600',
} as const;
